import React from "react";
import { IntegrationsStatus } from "@/api/getIntegrationsStatus/types";
import DataSourceCard from "../DataSourceCard/DataSourceCard";
import ConfigurationStep from "../ConfigurationStep/ConfigurationStep";
import QuickBooksConnection from "../QuickBooksConnection/QuickBooksConnection";
import QuickBooksTradeConfiguration from "../QuickBooksTradeConfiguration/QuickBooksTradeConfiguration";
import { MembershipSkuSection } from "@/modules/hub/features/MembershipSkuManagement/components/MembershipSkuSection/MembershipSkuSection";

interface QuickBooksDataSourceProps {
  companyUuid: string;
  intacctId?: string;
  isExpanded: boolean;
  onToggle: () => void;
  status?: IntegrationsStatus["quickBooksOnlineIntegration"];
}

const QuickBooksDataSource: React.FC<QuickBooksDataSourceProps> = ({
  companyUuid,
  intacctId,
  isExpanded,
  onToggle,
  status,
}) => {
  const isConnected = status?.isConnected ?? false;
  const hasSyncableData = status?.hasSyncableData ?? false;
  const hasRunningJobs = status?.hasRunningJobs ?? false;
  const isCategorized =
    (status?.isTradeConfigured ?? false) &&
    (status?.hasActiveMembershipSkus ?? false);

  const syncStatusLabel = hasRunningJobs
    ? "A sync is currently running."
    : hasSyncableData
      ? "QuickBooks data has been imported."
      : "No QuickBooks data has been synced yet.";

  return (
    <DataSourceCard
      capabilities={[
        { label: "Financial KPIs", isActive: isConnected },
        { label: "Customer leads", isActive: isConnected },
      ]}
      id="quickbooks"
      isConnected={isConnected}
      isExpanded={isExpanded}
      logo={
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#2ca01c] text-sm font-bold text-white">
          qb
        </span>
      }
      name="QuickBooks Online"
      onToggle={onToggle}
    >
      <ConfigurationStep
        description="Authorize a secure, read-only connection. This powers both financial reports and customer import."
        isComplete={isConnected}
        number={1}
        title="Connect your account"
      >
        <QuickBooksConnection intacctId={intacctId} />
      </ConfigurationStep>

      <ConfigurationStep
        description="Balance Sheets and Profit & Loss statements sync automatically each month for KPIs and coach review."
        isComplete={hasSyncableData}
        number={2}
        title="Sync financial data"
      >
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {syncStatusLabel}
        </p>
      </ConfigurationStep>

      <ConfigurationStep
        description="Choose the trade and membership SKUs used to categorize imported invoices."
        isComplete={isCategorized}
        number={3}
        title="Categorize invoices"
      >
        <div className="space-y-6">
          <QuickBooksTradeConfiguration
            companyUuid={companyUuid}
            isTradeEditable={status?.isTradeEditable ?? false}
          />
          <MembershipSkuSection currentTenant={intacctId} />
        </div>
      </ConfigurationStep>

      <ConfigurationStep
        description="Contact the support team to initiate a QuickBooks Online data import into your account."
        number={4}
        title="Request data import"
      />

      <div className="flex items-center gap-2 border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-slate-700 dark:text-gray-400">
        <svg
          className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        Secure OAuth, read-only. We never store your QuickBooks credentials.
      </div>
    </DataSourceCard>
  );
};

export default QuickBooksDataSource;
